import {
  type BackgroundRender,
  type BannerRender,
  type CharacterRender,
  type Collection,
  type ExplosionRender,
  type FrameRender,
  type HatRender,
  type Item,
  type Slot,
  type TrailRender,
  COLLECTIONS,
  CUSTOM_ACCESS_ID,
  collectionOf,
  getBySlot,
  getItem,
  isRestricted,
} from "../../../shared/shop-catalog";
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import {
  drawCard,
  drawSceneBackground,
  drawSceneTitle,
  fillRoundedRect,
  strokeRoundedRect,
} from "../../utils/draw";
import { drawBannerBg } from "../entities/BannerAccessory";
import { drawCharacterSprite } from "../entities/CharacterSprite";
import { drawCustomGrid } from "../entities/CustomArt";
import { drawExplosion } from "../entities/Explosion";
import { drawFrame } from "../entities/FrameAccessory";
import { drawHatBack, drawHatFront } from "../entities/HatAccessory";
import { drawTrailIcon } from "../entities/TrailIcon";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import { CUSTOM_SLOTS, customIdFor, type CustomSlot } from "../systems/CustomStore";
import { CustomEditorScene } from "./CustomEditorScene";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

type Tab = "character" | "banner" | "cosmetics" | "background" | "collections";
type CosmeticSlot = "hat" | "trail" | "frame" | "explosion";

const CUSTOM_SLOT_TITLE: Record<CustomSlot, string> = {
  character: "Custom Character",
  hat: "Custom Hat",
  banner: "Custom Banner",
  trail: "Custom Trail",
  frame: "Custom Frame",
  background: "Custom Scenery",
  explosion: "Custom Explosion",
};

const CUSTOM_SLOT_SET = new Set<string>(CUSTOM_SLOTS);

const SESSION_MSG = "Your session expired. Go to the menu and log in again.";

const TABS: { id: Tab; label: string }[] = [
  { id: "character", label: "Characters" },
  { id: "banner", label: "Banners" },
  { id: "cosmetics", label: "Cosmetics" },
  { id: "background", label: "Scenery" },
  { id: "collections", label: "Collections" },
];

const SUB_TABS: { id: CosmeticSlot; label: string }[] = [
  { id: "hat", label: "Hat" },
  { id: "trail", label: "Trail" },
  { id: "frame", label: "Frame" },
  { id: "explosion", label: "Boom" },
];

type BgSlot = "background" | "background_night";
const BG_SUB_TABS: { id: BgSlot; label: string }[] = [
  { id: "background", label: "Day" },
  { id: "background_night", label: "Night" },
];

const CARD_X = 20;
const CARD_W = 320;
const ROW_H = 52;
const ROW_GAP = 6;
const LIST_BOTTOM = 612;

export class ShopScene implements Scene {
  private tab: Tab = "character";
  private sub: CosmeticSlot = "hat";
  private bgSub: BgSlot = "background";
  private listTop = 0;
  private listH = 0;
  private scrollY = 0;
  private maxScroll = 0;
  private dragLastY: number | null = null;
  // Guards async buy/equip handlers from re-stamping buttons after the scene left.
  private entered = false;

  constructor(private readonly ctxMgr: GameContext) {}

  enter(): void {
    this.entered = true;
    this.rebuild();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onDragStart);
    window.addEventListener("pointermove", this.onDragMove);
    window.addEventListener("pointerup", this.onDragEnd);
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.removeEventListener("wheel", this.onWheel);
    canvas.removeEventListener("pointerdown", this.onDragStart);
    window.removeEventListener("pointermove", this.onDragMove);
    window.removeEventListener("pointerup", this.onDragEnd);
  }

  update(_dt: number): void {
    // static
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    drawSceneTitle(ctx, "SHOP");

    // wallet badge
    const w = 110;
    const wx = VIRTUAL_WIDTH - w - 8;
    const wy = 8;
    ctx.fillStyle = "rgba(245,233,200,0.95)";
    fillRoundedRect(ctx, wx, wy, w, 28, 8);
    ctx.strokeStyle = "#7a5e2a";
    ctx.lineWidth = 2;
    strokeRoundedRect(ctx, wx + 0.5, wy + 0.5, w - 1, 27, 8);
    ctx.fillStyle = "#7a5e2a";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(`Flies: ${this.ctxMgr.shop.wallet}`, wx + w / 2, wy + 14);
    ctx.textBaseline = "alphabetic";

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.fillStyle = "#f5e9c8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Log in to use the shop.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    if (this.tab === "collections") this.renderCollections(ctx);
    else this.renderItems(ctx);
    this.renderScrollIndicator(ctx);
  }

  private visibleItems(): Item[] {
    const shop = this.ctxMgr.shop;
    return getBySlot(this.currentSlot()).filter(
      (it) => it.id !== CUSTOM_ACCESS_ID && (!isRestricted(it.id) || shop.owns(it.id)),
    );
  }

  /** The CustomSlot for the current tab, or null (no custom row shown). */
  private customSlotForCurrent(): CustomSlot | null {
    if (!this.ctxMgr.shop.hasCustomAccess()) return null;
    const slot = this.currentSlot();
    return CUSTOM_SLOT_SET.has(slot) ? (slot as CustomSlot) : null;
  }

  /**
   * The equipped catalog id for a slot, treating a slot as "nothing equipped"
   * while its custom drawing is the active cosmetic — so no catalog row claims
   * to be equipped at the same time as the custom row.
   */
  private equippedCatalogId(slot: Slot): string | null {
    if (CUSTOM_SLOT_SET.has(slot) && this.ctxMgr.shop.custom.isEquipped(slot as CustomSlot)) {
      return null;
    }
    return this.ctxMgr.shop.equippedId(slot);
  }

  private renderItems(ctx: CanvasRenderingContext2D): void {
    const items = this.visibleItems();
    const customSlot = this.customSlotForCurrent();
    const offset = customSlot ? 1 : 0;
    const totalRows = items.length + offset;
    if (totalRows === 0) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(CARD_X - 2, this.listTop, CARD_W + 4, this.listH);
    ctx.clip();

    const stride = ROW_H + ROW_GAP;
    const firstVisible = Math.max(0, Math.floor(this.scrollY / stride) - 1);
    const lastVisible = Math.min(totalRows - 1, Math.ceil((this.scrollY + this.listH) / stride) + 1);

    const shop = this.ctxMgr.shop;
    for (let r = firstVisible; r <= lastVisible; r += 1) {
      const rowY = this.listTop + r * stride - this.scrollY;
      if (customSlot && r === 0) {
        this.drawCustomRow(ctx, customSlot, rowY);
        continue;
      }
      const item = items[r - offset]!;
      const owned = shop.owns(item.id);
      const equipped = this.equippedCatalogId(item.slot) === item.id;
      const affordable = shop.wallet >= item.price;

      drawRowCard(ctx, item, rowY, owned, equipped);
      const { label, style } = actionFor(owned, equipped, affordable);
      const actionW = 64;
      const actionH = 28;
      const actionX = CARD_X + CARD_W - actionW - 10;
      const actionY = rowY + (ROW_H - actionH) / 2;
      drawActionButton(ctx, label, actionX, actionY, actionW, actionH, false, style);
    }

    ctx.restore();
  }

  /** Geometry of the two right-hand buttons on the custom row. */
  private customRowRects(rowY: number): {
    edit: { x: number; y: number; w: number; h: number };
    equip: { x: number; y: number; w: number; h: number };
  } {
    const h = 28;
    const y = rowY + (ROW_H - h) / 2;
    const equip = { x: CARD_X + CARD_W - 60 - 10, y, w: 60, h };
    const edit = { x: equip.x - 6 - 46, y, w: 46, h };
    return { edit, equip };
  }

  private drawCustomRow(ctx: CanvasRenderingContext2D, slot: CustomSlot, rowY: number): void {
    const shop = this.ctxMgr.shop;
    const equipped = shop.custom.isEquipped(slot);
    const hasDesign = shop.custom.design(slot) !== null;

    ctx.fillStyle = equipped ? "rgba(122,94,42,0.95)" : "rgba(245,233,200,0.95)";
    fillRoundedRect(ctx, CARD_X, rowY, CARD_W, ROW_H, 10);
    ctx.strokeStyle = "#7a5e2a";
    ctx.lineWidth = 2;
    strokeRoundedRect(ctx, CARD_X + 0.5, rowY + 0.5, CARD_W - 1, ROW_H - 1, 10);

    // teal "your drawing" glow border
    ctx.save();
    ctx.shadowColor = "#3ad1c8";
    ctx.shadowBlur = 8;
    ctx.strokeStyle = "#3ad1c8";
    ctx.lineWidth = 2;
    strokeRoundedRect(ctx, CARD_X + 1, rowY + 1, CARD_W - 2, ROW_H - 2, 10);
    ctx.restore();

    // swatch: the raw drawing (or a placeholder)
    const sx = CARD_X + 10;
    const sy = rowY + (ROW_H - 32) / 2;
    ctx.fillStyle = "#0e1118";
    fillRoundedRect(ctx, sx, sy, 32, 32, 6);
    if (hasDesign) {
      const d = shop.custom.cellsOrBlank(slot);
      ctx.save();
      ctx.beginPath();
      ctx.rect(sx, sy, 32, 32);
      ctx.clip();
      drawCustomGrid(ctx, d.cells, d.grid, sx, sy, 32, 32);
      ctx.restore();
    }

    const textColor = equipped ? "#f5e9c8" : "#1a1f2b";
    ctx.fillStyle = textColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(CUSTOM_SLOT_TITLE[slot], CARD_X + 52, rowY + ROW_H / 2 - 8);
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillText(
      equipped ? "equipped" : hasDesign ? "your drawing" : "not drawn yet",
      CARD_X + 52,
      rowY + ROW_H / 2 + 10,
    );
    ctx.textBaseline = "alphabetic";

    const { edit, equip } = this.customRowRects(rowY);
    drawActionButton(ctx, hasDesign ? "Edit" : "Draw", edit.x, edit.y, edit.w, edit.h, false, "primary");
    const equipStyle = hasDesign ? (equipped ? "subtle" : "primary") : "muted";
    drawActionButton(ctx, equipped ? "On" : "Equip", equip.x, equip.y, equip.w, equip.h, false, equipStyle);
  }

  private renderCollections(ctx: CanvasRenderingContext2D): void {
    const shop = this.ctxMgr.shop;
    ctx.save();
    ctx.beginPath();
    ctx.rect(CARD_X - 2, this.listTop, CARD_W + 4, this.listH);
    ctx.clip();

    const stride = ROW_H + ROW_GAP;
    const time = performance.now() / 1000;
    COLLECTIONS.forEach((coll, i) => {
      const rowY = this.listTop + i * stride - this.scrollY;
      if (rowY + ROW_H < this.listTop || rowY > this.listTop + this.listH) return;

      const owned = coll.ids.filter((id) => shop.owns(id)).length;
      const total = coll.ids.length;
      const complete = owned === total;
      const color = coll.rainbow ? `hsl(${((time * 60) % 360).toFixed(0)}, 90%, 60%)` : coll.color;

      ctx.fillStyle = complete ? "rgba(122,94,42,0.95)" : "rgba(245,233,200,0.95)";
      fillRoundedRect(ctx, CARD_X, rowY, CARD_W, ROW_H, 10);
      ctx.strokeStyle = "#7a5e2a";
      ctx.lineWidth = 2;
      strokeRoundedRect(ctx, CARD_X + 0.5, rowY + 0.5, CARD_W - 1, ROW_H - 1, 10);
      if (owned > 0) {
        ctx.save();
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        strokeRoundedRect(ctx, CARD_X + 1, rowY + 1, CARD_W - 2, ROW_H - 2, 10);
        ctx.restore();
      }

      // character preview swatch
      const charItem = coll.ids.map((id) => getItem(id)).find((it) => it?.slot === "character");
      if (charItem && charItem.slot === "character") {
        drawCharacterSwatch(ctx, charItem.render, CARD_X + 10, rowY + (ROW_H - 32) / 2, 32);
      }

      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle = color;
      ctx.font = "bold 15px system-ui, sans-serif";
      ctx.fillText(coll.name, CARD_X + 52, rowY + 16);
      ctx.font = "12px system-ui, sans-serif";
      ctx.fillStyle = complete ? "#f5e9c8" : "#7a5e2a";
      ctx.fillText(requirementText(coll), CARD_X + 52, rowY + 36);

      ctx.textAlign = "right";
      ctx.font = "bold 12px system-ui, sans-serif";
      let status: string;
      let statusColor: string;
      if (complete) {
        status = "✓ Complete";
        statusColor = "#1b7a3a";
      } else if (owned > 0) {
        status = `${owned}/${total}`;
        statusColor = "#7a5e2a";
      } else {
        status = this.lockStatus(coll);
        statusColor = "#a06a2a";
      }
      ctx.fillStyle = statusColor;
      ctx.fillText(status, CARD_X + CARD_W - 12, rowY + ROW_H / 2);
      ctx.textBaseline = "alphabetic";
    });

    ctx.restore();
  }

  private lockStatus(coll: Collection): string {
    if (coll.unlock?.metric === "score") return `best ${this.ctxMgr.score.high}/${coll.unlock.goal}`;
    return "Locked";
  }

  private renderScrollIndicator(ctx: CanvasRenderingContext2D): void {
    if (this.maxScroll <= 0) return;
    const trackX = CARD_X + CARD_W + 4;
    const trackH = this.listH;
    const thumbH = Math.max(20, (this.listH / (this.maxScroll + this.listH)) * trackH);
    const t = this.scrollY / this.maxScroll;
    const thumbY = this.listTop + t * (trackH - thumbH);
    ctx.fillStyle = "rgba(245,233,200,0.25)";
    ctx.fillRect(trackX, this.listTop, 3, trackH);
    ctx.fillStyle = "rgba(245,233,200,0.85)";
    ctx.fillRect(trackX, thumbY, 3, thumbH);
  }

  private goBack(): void {
    this.ctxMgr.goTo(new MenuScene(this.ctxMgr));
  }

  private readonly onWheel = (e: WheelEvent): void => {
    if (this.maxScroll <= 0) return;
    e.preventDefault();
    this.scrollY = clamp(this.scrollY + e.deltaY, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragStart = (e: PointerEvent): void => {
    if (this.maxScroll <= 0) return;
    const { x, y } = this.ctxMgr.renderer.clientToCanvas(e.clientX, e.clientY);
    if (
      x >= CARD_X &&
      x <= CARD_X + CARD_W &&
      y >= this.listTop &&
      y <= this.listTop + this.listH
    ) {
      this.dragLastY = e.clientY;
    }
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    if (this.dragLastY === null) return;
    const dyClient = e.clientY - this.dragLastY;
    this.dragLastY = e.clientY;
    const rect = this.ctxMgr.renderer.canvas.getBoundingClientRect();
    const dyCanvas = (dyClient / rect.height) * VIRTUAL_HEIGHT;
    this.scrollY = clamp(this.scrollY - dyCanvas, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragEnd = (): void => {
    this.dragLastY = null;
  };

  private currentSlot(): Slot {
    if (this.tab === "character") return "character";
    if (this.tab === "banner") return "banner";
    if (this.tab === "background") return this.bgSub;
    return this.sub;
  }

  private rebuild(): void {
    const buttons: Button[] = [];

    buttons.push(backButton(() => this.goBack()));

    // top-level tabs — width derived from the count so they always fit.
    const tabY = 64;
    const tabH = 28;
    const tabGap = 7;
    const tabSideMargin = 8;
    const tabW = Math.floor(
      (VIRTUAL_WIDTH - tabSideMargin * 2 - (TABS.length - 1) * tabGap) / TABS.length,
    );
    const tabsTotal = TABS.length * tabW + (TABS.length - 1) * tabGap;
    const tabsStart = (VIRTUAL_WIDTH - tabsTotal) / 2;
    TABS.forEach((t, i) => {
      const x = tabsStart + i * (tabW + tabGap);
      const active = this.tab === t.id;
      buttons.push({
        x,
        y: tabY,
        w: tabW,
        h: tabH,
        draw: (ctx, hit) => drawTabButton(ctx, t.label, x, tabY, tabW, tabH, hit, active),
        onPress: () => {
          if (this.tab !== t.id) {
            this.tab = t.id;
            this.scrollY = 0;
            this.rebuild();
          }
        },
      });
    });

    let listTop = tabY + tabH + 12;

    // sub-tabs (cosmetics only)
    if (this.tab === "cosmetics") {
      const subY = listTop;
      const subGap = 8;
      const subW = Math.floor((VIRTUAL_WIDTH - 16 - (SUB_TABS.length - 1) * subGap) / SUB_TABS.length);
      const subH = 24;
      const subTotal = SUB_TABS.length * subW + (SUB_TABS.length - 1) * subGap;
      const subStart = (VIRTUAL_WIDTH - subTotal) / 2;
      SUB_TABS.forEach((t, i) => {
        const x = subStart + i * (subW + subGap);
        const active = this.sub === t.id;
        buttons.push({
          x,
          y: subY,
          w: subW,
          h: subH,
          draw: (ctx, hit) => drawTabButton(ctx, t.label, x, subY, subW, subH, hit, active),
          onPress: () => {
            if (this.sub !== t.id) {
              this.sub = t.id;
              this.scrollY = 0;
              this.rebuild();
            }
          },
        });
      });
      listTop += subH + 10;
    }

    // sub-tabs (scenery: Day / Night)
    if (this.tab === "background") {
      const subY = listTop;
      const subW = 84;
      const subH = 24;
      const subGap = 8;
      const subTotal = BG_SUB_TABS.length * subW + (BG_SUB_TABS.length - 1) * subGap;
      const subStart = (VIRTUAL_WIDTH - subTotal) / 2;
      BG_SUB_TABS.forEach((t, i) => {
        const x = subStart + i * (subW + subGap);
        const active = this.bgSub === t.id;
        buttons.push({
          x,
          y: subY,
          w: subW,
          h: subH,
          draw: (ctx, hit) => drawTabButton(ctx, t.label, x, subY, subW, subH, hit, active),
          onPress: () => {
            if (this.bgSub !== t.id) {
              this.bgSub = t.id;
              this.scrollY = 0;
              this.rebuild();
            }
          },
        });
      });
      listTop += subH + 10;
    }

    const stride = ROW_H + ROW_GAP;
    this.listTop = listTop;
    this.listH = LIST_BOTTOM - listTop;

    // The Collections tab is an informational list (no per-row action buttons) —
    // just size the scroll region and we're done.
    if (this.tab === "collections") {
      this.maxScroll = Math.max(0, COLLECTIONS.length * stride - ROW_GAP - this.listH);
      if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;
      this.ctxMgr.buttons.set(buttons);
      return;
    }

    // item rows — visuals are painted in render() with a clip; here we only
    // register hit-test rects for the action button(s) on each visible row.
    const items = this.visibleItems();
    const customSlot = this.customSlotForCurrent();
    const offset = customSlot ? 1 : 0;
    this.maxScroll = Math.max(0, (items.length + offset) * stride - ROW_GAP - this.listH);
    if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;

    const shop = this.ctxMgr.shop;
    const visible = (y: number, h: number): boolean =>
      y >= this.listTop && y + h <= this.listTop + this.listH;

    // Leading custom row: Edit + Equip.
    if (customSlot) {
      const rowY = listTop + 0 * stride - this.scrollY;
      const { edit, equip } = this.customRowRects(rowY);
      const slot = customSlot;
      if (visible(edit.y, edit.h)) {
        buttons.push({
          ...edit,
          draw: () => undefined,
          onPress: () => this.ctxMgr.goTo(new CustomEditorScene(this.ctxMgr, slot)),
        });
        buttons.push({
          ...equip,
          draw: () => undefined,
          onPress: () => {
            if (shop.custom.design(slot) === null) return;
            void this.handleEquip(slot, shop.custom.isEquipped(slot) ? null : customIdFor(slot));
          },
        });
      }
    }

    items.forEach((item, i) => {
      const rowY = listTop + (i + offset) * stride - this.scrollY;
      const actionW = 64;
      const actionH = 28;
      const actionX = CARD_X + CARD_W - actionW - 10;
      const actionY = rowY + (ROW_H - actionH) / 2;
      if (actionY < this.listTop || actionY + actionH > this.listTop + this.listH) return;

      const owned = shop.owns(item.id);
      const equipped = this.equippedCatalogId(item.slot) === item.id;
      const affordable = shop.wallet >= item.price;
      const { onPress } = this.actionDispatchFor(item, owned, equipped, affordable);
      buttons.push({
        x: actionX,
        y: actionY,
        w: actionW,
        h: actionH,
        draw: () => undefined,
        onPress,
      });
    });

    this.ctxMgr.buttons.set(buttons);
  }

  private actionDispatchFor(
    item: Item,
    owned: boolean,
    equipped: boolean,
    affordable: boolean,
  ): { onPress: () => void } {
    if (!owned && affordable) return { onPress: () => void this.handleBuy(item.id) };
    if (!owned) return { onPress: () => undefined };
    if (!equipped) return { onPress: () => void this.handleEquip(item.slot, item.id) };
    return { onPress: () => void this.handleEquip(item.slot, null) };
  }

  private async handleBuy(itemId: string): Promise<void> {
    const res = await this.ctxMgr.shop.buy(itemId);
    if (!res.ok) {
      const msg =
        res.error === "insufficient_funds"
          ? "Not enough flies."
          : res.error === "session_expired" || res.error === "not_logged_in"
            ? SESSION_MSG
            : "Couldn't complete the purchase.";
      window.alert(msg);
    }
    if (this.entered) this.rebuild();
  }

  private async handleEquip(slot: Slot, itemId: string | null): Promise<void> {
    const res = await this.ctxMgr.shop.equip(slot, itemId);
    if (!res.ok) {
      const msg =
        res.error === "session_expired" || res.error === "not_logged_in"
          ? SESSION_MSG
          : res.error === "not_owned"
            ? "You don't own that item yet."
            : "Couldn't update equipped item.";
      window.alert(msg);
    }
    if (this.entered) this.rebuild();
  }
}

function drawTabButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  w: number,
  h: number,
  hit: boolean,
  active: boolean,
): void {
  ctx.fillStyle = active || hit ? "#7a5e2a" : "rgba(245,233,200,0.9)";
  fillRoundedRect(ctx, x, y, w, h, 8);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 2;
  strokeRoundedRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 8);
  ctx.fillStyle = active || hit ? "#f5e9c8" : "#1a1f2b";
  // Shrink the font until the label fits the (possibly narrow) tab width.
  let fs = 13;
  do {
    ctx.font = `bold ${fs}px system-ui, sans-serif`;
    if (ctx.measureText(label).width <= w - 6) break;
    fs -= 1;
  } while (fs > 8);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, y + h / 2);
  ctx.textBaseline = "alphabetic";
}

function requirementText(coll: Collection): string {
  if (coll.unlock) {
    return coll.unlock.metric === "flies"
      ? `${coll.unlock.goal} flies in a run`
      : `${coll.unlock.goal} pipes in a run`;
  }
  if (coll.id === "creator") return "CREATOR only";
  if (coll.id === "custom") return "Win the diorama challenge";
  return "Buy in shop";
}

function drawRowCard(
  ctx: CanvasRenderingContext2D,
  item: Item,
  rowY: number,
  owned: boolean,
  equipped: boolean,
): void {
  drawCard(ctx, CARD_X, rowY, CARD_W, ROW_H, 10, equipped ? { fill: "rgba(122,94,42,0.95)" } : {});

  drawSwatch(ctx, item, CARD_X + 10, rowY + (ROW_H - 32) / 2);

  const textColor = equipped ? "#f5e9c8" : "#1a1f2b";
  ctx.fillStyle = textColor;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.font = "bold 14px system-ui, sans-serif";
  ctx.fillText(item.name, CARD_X + 52, rowY + ROW_H / 2 - 8);

  ctx.font = "12px system-ui, sans-serif";
  const sub = owned ? (equipped ? "equipped" : "owned") : `${item.price} flies`;
  ctx.fillText(sub, CARD_X + 52, rowY + ROW_H / 2 + 10);
  ctx.textBaseline = "alphabetic";

  // collection items get a glowing colored border so they stand out
  const coll = collectionOf(item.id);
  if (coll) {
    const color = coll.rainbow
      ? `hsl(${((performance.now() / 1000) * 60) % 360}, 90%, 60%)`
      : coll.color;
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    strokeRoundedRect(ctx, CARD_X + 1, rowY + 1, CARD_W - 2, ROW_H - 2, 10);
    ctx.restore();
  }
}

function drawSwatch(ctx: CanvasRenderingContext2D, item: Item, x: number, y: number): void {
  const size = 32;
  if (item.slot === "character") {
    drawCharacterSwatch(ctx, item.render, x, y, size);
  } else if (item.slot === "banner") {
    paintBannerSwatch(ctx, item.render, x, y, size, size);
  } else if (item.slot === "hat") {
    drawHatSwatch(ctx, item.render, x, y, size);
  } else if (item.slot === "trail") {
    drawTrailSwatch(ctx, item.render, x, y, size);
  } else if (item.slot === "frame") {
    drawFrameSwatch(ctx, item.render, x, y, size);
  } else if (item.slot === "explosion") {
    drawExplosionSwatch(ctx, item.render, x, y, size);
  } else {
    drawBackgroundSwatch(ctx, item.render, x, y, size);
  }
}

// Deterministic spark set so the looping swatch animates without per-frame jitter.
const SWATCH_SPARKS = Array.from({ length: 16 }, (_, i) => ({
  angle: (i / 16) * Math.PI * 2,
  dist: 10 + (i % 3) * 5,
  size: 1.6 + (i % 2),
  rot: i,
  seed: (i * 0.137) % 1,
}));

function drawExplosionSwatch(
  ctx: CanvasRenderingContext2D,
  cfg: ExplosionRender,
  x: number,
  y: number,
  size: number,
): void {
  ctx.fillStyle = "#0e1118";
  fillRoundedRect(ctx, x, y, size, size, 6);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, size, size);
  ctx.clip();
  ctx.translate(x + size / 2, y + size / 2);
  ctx.scale(0.4, 0.4);
  const loop = 0.9;
  const p = ((performance.now() / 1000) % loop) / loop;
  drawExplosion(ctx, cfg, 0, 0, p, SWATCH_SPARKS);
  ctx.restore();
}

function drawBackgroundSwatch(
  ctx: CanvasRenderingContext2D,
  cfg: BackgroundRender,
  x: number,
  y: number,
  size: number,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, size, size);
  ctx.clip();

  // resolve colors — rainbow (CREATOR) skies cycle the hue wheel
  let skyTop = cfg.skyTop;
  let skyBottom = cfg.skyBottom;
  let treeNear = cfg.treeNear;
  let sun = cfg.sun;
  if (cfg.rainbow) {
    const t = performance.now() / 1000;
    const L = cfg.isNight ? 40 : 60;
    skyTop = `hsl(${((t * 40) % 360 + 360) % 360}, 85%, ${L}%)`;
    skyBottom = `hsl(${((t * 40 + 70) % 360 + 360) % 360}, 85%, ${L}%)`;
    treeNear = `hsl(${((t * 40 + 250) % 360 + 360) % 360}, 70%, ${cfg.isNight ? 9 : 20}%)`;
    sun = "#ffffff";
  }

  // sky gradient
  const grad = ctx.createLinearGradient(0, y, 0, y + size);
  grad.addColorStop(0, skyTop);
  grad.addColorStop(1, skyBottom);
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, size, size);

  if (cfg.isNight) {
    ctx.fillStyle = "#fff";
    for (const [sx, sy] of [
      [6, 6],
      [20, 4],
      [26, 14],
      [12, 12],
    ] as const) {
      ctx.fillRect(x + sx, y + sy, 1, 1);
    }
  }

  // sun / moon
  ctx.fillStyle = sun;
  ctx.beginPath();
  ctx.arc(x + size * 0.74, y + size * 0.26, cfg.isNight ? 3 : 4, 0, Math.PI * 2);
  ctx.fill();

  // near treeline silhouette
  const baseY = y + size;
  ctx.fillStyle = treeNear;
  ctx.beginPath();
  ctx.moveTo(x, baseY);
  const tw = size / 4;
  for (let i = 0; i < 4; i += 1) {
    const tx = x + i * tw;
    ctx.lineTo(tx, baseY);
    ctx.lineTo(tx + tw / 2, baseY - size * 0.32 - (i % 2) * 3);
    ctx.lineTo(tx + tw, baseY);
  }
  ctx.lineTo(x + size, baseY);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

function paintBannerSwatch(
  ctx: CanvasRenderingContext2D,
  cfg: BannerRender,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  drawBannerBg(ctx, cfg, x, y, w, h, performance.now() / 1000);
}

const SWATCH_LIZARD: import("../../../shared/shop-catalog").CharacterRender = {
  kind: "lizard",
  body: "#4caf50",
  belly: "#aed581",
  tail: "#388e3c",
  legs: "#2e7d32",
};

function drawHatSwatch(
  ctx: CanvasRenderingContext2D,
  hat: HatRender,
  x: number,
  y: number,
  size: number,
): void {
  ctx.fillStyle = "#1a1f2b";
  fillRoundedRect(ctx, x, y, size, size, 8);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, size, size);
  ctx.clip();
  ctx.translate(x + size / 2, y + size * 0.6);
  ctx.scale(0.42, 0.42);
  const anim = { propAngle: 0.6, bobTime: 0.4, flapTime: 0 };
  drawHatBack(ctx, 14, hat, anim);
  drawCharacterSprite(ctx, 14, SWATCH_LIZARD, { bobTime: 0.4, flapTime: 0 });
  drawHatFront(ctx, 14, hat, anim);
  ctx.restore();
}

function drawTrailSwatch(
  ctx: CanvasRenderingContext2D,
  cfg: TrailRender,
  x: number,
  y: number,
  size: number,
): void {
  ctx.fillStyle = "#0e1118";
  fillRoundedRect(ctx, x, y, size, size, 6);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, size, size);
  ctx.clip();
  for (let i = 0; i < 5; i += 1) {
    const px = x + size - 4 - i * 5;
    const py = y + size / 2 + (i % 2 === 0 ? -2 : 2);
    drawTrailIcon(ctx, cfg, px, py, 1 - i * 0.18);
  }
  ctx.restore();
}

function drawFrameSwatch(
  ctx: CanvasRenderingContext2D,
  cfg: FrameRender,
  x: number,
  y: number,
  size: number,
): void {
  ctx.fillStyle = "#f5e9c8";
  fillRoundedRect(ctx, x + 4, y + 4, size - 8, size - 8, 4);
  drawFrame(
    ctx,
    cfg,
    { x: x + 1, y: y + 1, w: size - 2, h: size - 2, radius: 6 },
    performance.now() / 1000,
  );
  ctx.lineWidth = 1;
}

function drawActionButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  w: number,
  h: number,
  hit: boolean,
  style: "primary" | "muted" | "subtle",
): void {
  let fill: string;
  let text: string;
  if (style === "muted") {
    fill = "rgba(80,80,80,0.6)";
    text = "#bbbbbb";
  } else if (style === "subtle") {
    fill = hit ? "#1a1f2b" : "rgba(122,94,42,0.7)";
    text = "#f5e9c8";
  } else {
    fill = hit ? "#1a1f2b" : "#7a5e2a";
    text = "#f5e9c8";
  }
  ctx.fillStyle = fill;
  fillRoundedRect(ctx, x, y, w, h, 6);
  ctx.fillStyle = text;
  ctx.font = "bold 12px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, y + h / 2);
  ctx.textBaseline = "alphabetic";
}


function drawCharacterSwatch(
  ctx: CanvasRenderingContext2D,
  render: CharacterRender,
  x: number,
  y: number,
  size: number,
): void {
  ctx.fillStyle = "#1a1f2b";
  fillRoundedRect(ctx, x, y, size, size, 8);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, size, size);
  ctx.clip();
  ctx.translate(x + size / 2, y + size / 2);
  ctx.scale(0.42, 0.42);
  drawCharacterSprite(ctx, 14, render, { bobTime: 0.4, flapTime: 0 });
  ctx.restore();
}

function actionFor(
  owned: boolean,
  equipped: boolean,
  affordable: boolean,
): { label: string; style: "primary" | "muted" | "subtle" } {
  if (!owned && affordable) return { label: "Buy", style: "primary" };
  if (!owned) return { label: "Locked", style: "muted" };
  if (!equipped) return { label: "Equip", style: "primary" };
  return { label: "Equipped", style: "subtle" };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

